import type { Database } from '../types/database.types.js';
export type ProjectRole = Database['public']['Enums']['project_role'];

const TEAM_STATUS_MAP: Record<string, Record<ProjectRole, StatusDisplay>> = {
  pending: {
    editor: { label: 'Pending', colour: 'gray' },
    reviewer: { label: 'Pending', colour: 'gray' },
    producer: { label: 'Pending', colour: 'gray' },
  },
  processing: {
    editor: { label: 'Processing', colour: 'mauve', pulse: true },
    reviewer: { label: 'Processing', colour: 'mauve', pulse: true },
    producer: { label: 'Processing', colour: 'mauve', pulse: true },
  },
  in_progress: {
    editor: { label: 'In Progress', colour: 'blue' },
    reviewer: { label: 'In Progress', colour: 'blue' },
    producer: { label: 'In Progress', colour: 'blue' },
  },
  team_review: {
    editor: { label: 'With Producer', colour: 'violet' },
    reviewer: { label: 'Needs Review', colour: 'amber' },
    producer: { label: 'Needs Review', colour: 'amber' },
  },
  client_review: {
    editor: { label: 'With Client', colour: 'pink' },
    reviewer: { label: 'With Client', colour: 'pink' },
    producer: { label: 'With Client', colour: 'pink' },
  },
  editor_revision: {
    editor: { label: 'Needs Revision', colour: 'amber' },
    reviewer: { label: 'With Editor', colour: 'violet' },
    producer: { label: 'With Editor', colour: 'violet' },
  },
  approved: {
    editor: { label: 'Approved', colour: 'green' },
    reviewer: { label: 'Approved', colour: 'green' },
    producer: { label: 'Approved', colour: 'green' },
  },
  cancelled: {
    editor: { label: 'Cancelled', colour: 'stone' },
    reviewer: { label: 'Cancelled', colour: 'stone' },
    producer: { label: 'Cancelled', colour: 'stone' },
  },
  error: {
    editor: { label: '// Error //', colour: 'red' },
    reviewer: { label: '// Error //', colour: 'red' },
    producer: { label: '// Error //', colour: 'red' },
  },
};

const CLIENT_STATUS_MAP: Record<string, StatusDisplay> = {
  processing: { label: 'Preparing', colour: 'slate', pulse: true },
  client_review: { label: 'Needs Review', colour: 'amber' },
  pending: { label: 'Pending', colour: 'gray' },
  in_progress: { label: 'In Progress', colour: 'blue' },
  editor_revision: { label: 'Revising', colour: 'violet' },
  approved: { label: 'Approved', colour: 'green' },
  cancelled: { label: 'Cancelled', colour: 'stone' },
};

type StatusDisplay = { label: string; colour: string; pulse?: boolean };

export function getTeamStatusDisplay(
  status: string,
  projectRole: ProjectRole
): StatusDisplay {
  return (
    TEAM_STATUS_MAP[status]?.[projectRole] ?? {
      label: 'Unknown',
      colour: 'gray',
    }
  );
}

export function getClientStatusDisplay(status: string): StatusDisplay {
  return CLIENT_STATUS_MAP[status] ?? { label: 'Unknown', colour: 'gray' };
}

export function getProjectTeamStatus(
  teamStatuses: string[],
  projectRole: ProjectRole
): string {
  const priorityMap: Record<ProjectRole, string[]> = {
    editor: [
      'processing',
      'editor_revision',
      'in_progress',
      'team_review',
      'client_review',
      'pending',
      'approved',
      'cancelled',
    ],
    reviewer: [
      'processing',
      'team_review',
      'editor_revision',
      'client_review',
      'in_progress',
      'pending',
      'approved',
      'cancelled',
    ],
    producer: [
      'processing',
      'team_review',
      'editor_revision',
      'client_review',
      'in_progress',
      'pending',
      'approved',
      'cancelled',
    ],
  };

  const priority = priorityMap[projectRole];
  for (const status of priority) {
    if (teamStatuses.includes(status)) return status;
  }
  return 'error';
}

export function optimisticTeamStatus(
  formData: FormData | undefined
): string | null {
  const intent = formData?.get('intent');
  if (intent === 'teamApprove') return 'processing';
  if (intent === 'teamNeedsRevision') return 'editor_revision';
  return null;
}
